import React, { useState, useMemo, useEffect } from "react";
import { useFormik } from "formik";
import { RiDeleteBin5Line } from "react-icons/ri";
import Modal from "../../components/common/modal";
import { useNavigate, useParams } from "react-router-dom";
import "../../assets/styles/tags.css";
import { useViewCategory } from "../../hooks/categorys/category.showall";
import { useViewIndustryQuery } from "../../hooks/industry/industryList";
import { useViewClientQuery } from "../../hooks/clients/viewClientList";
import { useCaseStudyStore } from "../../hooks/caseStudy/create.casestudy";
import { useCaseStudyUpdate } from "../../hooks/caseStudy/update.casestudy";
import { useViewSingleCaseStudy } from "../../hooks/caseStudy/view.single.caseStudy";
import { toast } from "react-hot-toast";
import { getCaseStudyValidationSchema } from "../../validation/caseStudyValidation";
import { useViewSubscription } from "../../hooks/subscription/subscription.view";
import {
  getPlanLimitMessage,
  getPlanMediaLimits,
  filterFilesForPlanLimit,
  formatPlanCap,
} from "../subscription/planLimits";
import { resolveImageUrl } from "../../utils/resolveImageUrl";

const AddCaseStudy = () => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState();
  // "http://i.pravatar.cc/500?img=7"

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const { data: singleCaseStudyData, isLoading: isProductLoading } =
    useViewSingleCaseStudy(id);

  useEffect(() => {
    if (singleCaseStudyData) {
      console.log("Case Study Data to Edit:", singleCaseStudyData);
    }
  }, [singleCaseStudyData]);

  const caseStudyEdit = useMemo(() => {
    if (!singleCaseStudyData) return null;
    return (
      singleCaseStudyData?.data?.caseStudy ||
      singleCaseStudyData?.caseStudy ||
      singleCaseStudyData?.data ||
      singleCaseStudyData
    );
  }, [singleCaseStudyData]);

  const initialFormValues = useMemo(() => {
    const empty = {
      title: "",
      description: "",
      category: "",
      industry: "",
      client: "",
      bulkImages: [],
    };

    if (isEditMode && caseStudyEdit) {
      const industry =
        caseStudyEdit.industry?._id ||
        caseStudyEdit.industry?.id ||
        caseStudyEdit.industry ||
        "";
      const client =
        caseStudyEdit.client?._id ||
        caseStudyEdit.client?.id ||
        caseStudyEdit.client ||
        "";

      return {
        ...empty,
        title: caseStudyEdit.title || "",
        description: caseStudyEdit.description || "",
        category:
          caseStudyEdit.category?._id ||
          caseStudyEdit.category?.id ||
          caseStudyEdit.category ||
          "",
        industry,
        client,
      };
    }

    return empty;
  }, [isEditMode, caseStudyEdit]);

  const readURL = (input) => {
    if (input.files && input.files[0]) {
      const file = input.files[0];
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onload = function (e) {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const [bulkImages, setBulkImages] = useState([]);
  const [uploadSave, setUploadSave] = useState(false);
  const [upload, setUload] = useState(false);
  const [bulkVideos, setBulkVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileType, setFileType] = useState("stone");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isFileDragging, setIsFileDragging] = useState(false);

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [bulkImageFiles, setBulkImageFiles] = useState([]);
  // const [catalogFiles, setCatalogFiles] = useState([]);
  const [bulkVideoFiles, setBulkVideoFiles] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [deletedVideoIds, setDeletedVideoIds] = useState([]);
  const [deletedCatalogIds, setDeletedCatalogIds] = useState([]);

  const [existingCatalogs, setExistingCatalogs] = useState([]);
  const [newCatalogFiles, setNewCatalogFiles] = useState([]);

  const { data: categoriesData } = useViewCategory();
  const { data: industriesData } = useViewIndustryQuery();
  const { data: clientData } = useViewClientQuery();
  const { mutate: createCaseStudy, isLoading: isCreating } =
    useCaseStudyStore();
  const { data: subscriptionResponse, isPlaceholderData } =
    useViewSubscription();
  const planLimits = getPlanMediaLimits(subscriptionResponse, {
    isReady: !isPlaceholderData,
  });
  const { mutate: updateCaseStudy, isLoading: isUpdating } =
    useCaseStudyUpdate();

  const resolveUrl = (raw) => resolveImageUrl(raw);

  const isInitialLoad = React.useRef(true);

  useEffect(() => {
    if (isEditMode && caseStudyEdit && isInitialLoad.current) {
      isInitialLoad.current = false;

      const getPath = (raw) => {
        if (!raw) return "";
        const path =
          typeof raw === "string"
            ? raw
            : raw.url ||
              raw.path ||
              raw.file ||
              raw.image ||
              raw.thumbnail_image ||
              "";

        // If it's an object with an _id, return an object so we can track the ID for deletions
        if (typeof raw === "object" && (raw._id || raw.id)) {
          return {
            _id: raw._id || raw.id,
            url: path.startsWith("http")
              ? path
              : path.startsWith("uploads")
                ? `/${path}`
                : path,
          };
        }

        if (typeof path !== "string") return "";
        if (path.startsWith("http")) return path;
        // Do not add leading slash here, resolveUrl will handle it
        if (path.startsWith("uploads")) return path;
        if (path.startsWith("/uploads")) return path.substring(1);
        return path;
      };

      // Handle Thumbnail
      const thumb =
        caseStudyEdit.thumbnail_image ||
        caseStudyEdit.thumbnail ||
        caseStudyEdit.image ||
        caseStudyEdit.media?.thumbnail ||
        caseStudyEdit.media?.thumbnail_image;

      if (thumb) {
        setImagePreview(resolveUrl(thumb));

        const thumbPath =
          typeof thumb === "string"
            ? thumb
            : thumb.url ||
              thumb.path ||
              thumb.image ||
              thumb.thumbnail_image ||
              "";

        setThumbnailFile(thumbPath);
      }

      // Handle Bulk Images
      const rawImages =
        caseStudyEdit.images ||
        caseStudyEdit.media?.images ||
        caseStudyEdit.media?.image ||
        [];

      if (Array.isArray(rawImages)) {
        setBulkImages(rawImages.map((img) => resolveUrl(img)));
        setBulkImageFiles(rawImages.map((img) => getPath(img)));
      }

      // Handle Videos
      const rawVideos =
        caseStudyEdit.videos || caseStudyEdit.media?.videos || [];

      if (Array.isArray(rawVideos)) {
        const uniqueVideos = [];
        const uniqueVideoFiles = [];
        const seenUrls = new Set();
        const autoDeletedIds = [];

        rawVideos.forEach((vid) => {
          const url = typeof vid === "string" ? vid : vid.url || vid.path || "";
          if (!seenUrls.has(url)) {
            seenUrls.add(url);
            uniqueVideos.push(resolveUrl(vid));
            uniqueVideoFiles.push({
              _id: typeof vid === "object" ? vid._id || vid.id : null,
              file: getPath(vid),
              title: vid.title || "",
              description: vid.description || "",
            });
          } else {
            if (typeof vid === "object" && (vid._id || vid.id)) {
              autoDeletedIds.push(vid._id || vid.id);
            }
          }
        });

        setBulkVideos(uniqueVideos);
        setBulkVideoFiles(uniqueVideoFiles);
        if (autoDeletedIds.length > 0) {
          setDeletedVideoIds((prev) => [
            ...new Set([...prev, ...autoDeletedIds]),
          ]);
        }
      }

      // Handle Catalogs
      const catalogs =
        caseStudyEdit.catlogs ||
        caseStudyEdit.catelogs ||
        caseStudyEdit.catalogs ||
        caseStudyEdit.media?.catlogs ||
        caseStudyEdit.media?.catelogs ||
        caseStudyEdit.media?.catalogs ||
        [];

      if (Array.isArray(catalogs)) {
        const initialUploadedFiles = catalogs.map((cat, index) => {
          const fileName =
            cat.title ||
            (typeof (cat.file || cat) === "string"
              ? (cat.file || cat).split("/").pop()
              : `File ${index + 1}`);
          return {
            id: cat._id || cat.id || index,
            name: fileName,
            size: "N/A",
            status: "uploaded",
          };
        });
        setUploadedFiles(initialUploadedFiles);
        setExistingCatalogs(catalogs);
      }

      // Handle Tags
      const rawTags = caseStudyEdit.tags || caseStudyEdit.media?.tags || [];
      if (Array.isArray(rawTags)) {
        setTags(rawTags.map((tag) => tag._id || tag.id || tag));
      }
    }
  }, [isEditMode, caseStudyEdit]);

  const categoriesList = useMemo(() => {
    if (!categoriesData) return [];
    const list =
      categoriesData?.data?.category ||
      categoriesData?.data?.categories ||
      categoriesData?.data ||
      categoriesData?.category ||
      categoriesData?.categories ||
      categoriesData ||
      [];
    return Array.isArray(list) ? list : [];
  }, [categoriesData]);

  const industriesList = useMemo(() => {
    if (!industriesData) return [];
    const list =
      industriesData?.data?.industry ||
      industriesData?.data?.industries ||
      industriesData?.data ||
      industriesData?.industry ||
      industriesData?.industries ||
      industriesData ||
      [];
    return Array.isArray(list) ? list : [];
  }, [industriesData]);

  const clientList = useMemo(() => {
    if (!clientData) return [];
    const list =
      clientData?.data?.client ||
      clientData?.data?.clients ||
      clientData?.data ||
      clientData?.client ||
      clientData?.clients ||
      clientData ||
      [];
    return Array.isArray(list) ? list : [];
  }, [clientData]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: initialFormValues,
    validationSchema: getCaseStudyValidationSchema(),
    onSubmit: (values) => {
      if (!isEditMode && !planLimits.caseStudies.canCreate) {
        toast.error(getPlanLimitMessage("caseStudies", planLimits));
        return;
      }

      if (
        Number.isFinite(planLimits.maxCaseStudyAttachments) &&
        bulkImageFiles.length > planLimits.maxCaseStudyAttachments
      ) {
        toast.error(
          `Attachment limit exceeded. Your plan allows ${planLimits.maxCaseStudyAttachments} image(s) per case study.`,
        );
        return;
      }

      const body = new FormData();
      body.append("title", values.title || "");
      body.append("description", values.description || "");
      body.append("category", values.category || "");
      body.append("industry", values.industry || "");
      body.append("client", values.client || "");

      bulkImageFiles.forEach((file) => {
        if (file instanceof File) {
          body.append("images", file);
        }
      });

      deletedImageIds.forEach((id) => {
        body.append("deletedImageIds", JSON.stringify(deletedImageIds));
      });

      if (isEditMode) {
        updateCaseStudy(
          { id, body },
          {
            onSuccess: () => {
              toast.success("Case Study updated successfully!");
              navigate("/case-studies");
            },
            onError: (err) => {
              toast.error(
                err?.response?.data?.message || "Failed to update case study",
              );
            },
          },
        );
      } else {
        createCaseStudy(body, {
          onSuccess: () => {
            toast.success("Case Study created successfully!");
            navigate("/case-studies");
          },
          onError: (err) => {
            toast.error(
              err?.response?.data?.message || "Failed to create case study",
            );
          },
        });
      }
    },
  });

  useEffect(() => {
    formik.setFieldValue("bulkImages", bulkImageFiles);
  }, [bulkImageFiles]);

  const getEmbedUrl = (url) => {
    if (
      typeof url === "string" &&
      (url.includes("youtube.com") || url.includes("youtu.be"))
    ) {
      let videoId = "";
      if (url.includes("watch?v=")) {
        videoId = url.split("watch?v=")[1].split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1].split("?")[0];
      }
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null;
  };

  const handleBulkImageChange = (e) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    if (!picked.length) return;

    const { accepted, error } = filterFilesForPlanLimit({
      files: picked,
      currentCount: bulkImageFiles.length,
      maxCount: planLimits.maxCaseStudyAttachments,
      maxSizeMB: planLimits.maxProductImageSizeMB,
      itemLabel: "image(s) per case study",
    });
    if (error) toast.error(error);
    if (!accepted.length) return;

    const nextFiles = [...bulkImageFiles, ...accepted];
    setBulkImageFiles(nextFiles);
    const newImages = accepted.map((file) => URL.createObjectURL(file));
    setBulkImages((prev) => [...prev, ...newImages]);
    formik.setFieldValue("bulkImages", nextFiles);
    formik.setFieldTouched("bulkImages", true, false);
    formik.validateField("bulkImages");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    if (imageFiles.length === 0) return;

    const { accepted, error } = filterFilesForPlanLimit({
      files: imageFiles,
      currentCount: bulkImageFiles.length,
      maxCount: planLimits.maxCaseStudyAttachments,
      maxSizeMB: planLimits.maxProductImageSizeMB,
      itemLabel: "image(s) per case study",
    });
    if (error) toast.error(error);
    if (!accepted.length) return;

    const nextFiles = [...bulkImageFiles, ...accepted];
    setBulkImageFiles(nextFiles);
    const newImages = accepted.map((file) => URL.createObjectURL(file));
    setBulkImages((prev) => [...prev, ...newImages]);
    formik.setFieldValue("bulkImages", nextFiles);
    formik.setFieldTouched("bulkImages", true, false);
    formik.validateField("bulkImages");
  };

  const handleAddVideoUrl = (e) => {
    e.preventDefault();

    if (videoUrl.trim()) {
      setBulkVideos((prev) => [...prev, videoUrl]);
      setBulkVideoFiles((prev) => [
        ...prev,
        { file: videoUrl, title: "", description: "" },
      ]);
      setVideoUrl("");
    }
  };

  const handleBulkVideoChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newFiles = files.map((file) => ({
        file: file,
        title: "",
        description: "",
      }));
      setBulkVideoFiles((prev) => [...prev, ...newFiles]);
      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setBulkVideos((prev) => [...prev, ...newPreviews]);
    }
  };

  const handleVideoMetaChange = (index, field, value) => {
    setBulkVideoFiles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  const uploadImages = () => {
    setUload(true);
  };

  const handleDeleteImage = (indexToDelete) => {
    const imageToDelete = bulkImageFiles[indexToDelete];

    console.log("DELETE IMAGE:", imageToDelete);

    // existing DB image
    if (
      imageToDelete &&
      typeof imageToDelete === "object" &&
      (imageToDelete._id || imageToDelete.id)
    ) {
      const imageId = imageToDelete._id || imageToDelete.id;

      setDeletedImageIds((prev) => [...prev, imageId]);
    }

    // remove instantly from UI
    const nextFiles = bulkImageFiles.filter(
      (_, index) => index !== indexToDelete,
    );
    setBulkImages((prev) => prev.filter((_, index) => index !== indexToDelete));
    setBulkImageFiles(nextFiles);
    formik.setFieldValue("bulkImages", nextFiles);
    formik.setFieldTouched("bulkImages", true, false);
    formik.validateField("bulkImages");
  };

  const handleImageClick = (img, index) => {
    setSelectedImage(img);
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    setSelectedImageIndex(null);
  };

  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(true);
  };

  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(
      (file) =>
        file.type === "application/pdf" ||
        file.type === "application/msword" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    );

    if (validFiles.length > 0) {
      setNewCatalogFiles((prev) => [...prev, ...validFiles]);
      const newFiles = validFiles.map((file, index) => ({
        id: Date.now() + index,
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
        progress: 100,
        status: "uploaded",
        timeLeft: 0,
      }));
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  return (
    <>
      <div className="body">
        <h2 style={{ marginBottom: "20px" }}>
          {isEditMode ? "Edit Case Study" : "Add Case Study"}
        </h2>

        <form onSubmit={formik.handleSubmit} noValidate>
          <div className="inputFields">
            <div className="formSection">
              <div className="form-header">
                <div
                  className="text-inputs"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  <div>
                    <label htmlFor="title">Title</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      placeholder="Text here..."
                      style={{ width: "100%", marginTop: "10px" }}
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <p className="error-text">{formik.errors.title}</p>
                    )}
                  </div>
                </div>
                {/* <div className="image-upload-section">
                <div className="avatar-upload" style={{ margin: "0" }}>
                  <div className="avatar-edit">
                    <input
                      type="file"
                      id="imageUpload"
                      accept=".png, .jpg, .jpeg"
                      onChange={(e) => readURL(e.target)}
                    />
                    <label htmlFor="imageUpload"></label>
                  </div>
                  <div className="avatar-preview">
                    <div
                      id="imagePreview"
                      style={{ backgroundImage: `url(${imagePreview})` }}
                    ></div>
                  </div>
                </div>
              </div> */}
              </div>

              <label htmlFor="description" className="description">
                Description
              </label>
              <br />
              <textarea
                id="description"
                name="description"
                placeholder="Type here..."
                rows="5"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              ></textarea>
              {formik.touched.description && formik.errors.description && (
                <p className="error-text">{formik.errors.description}</p>
              )}
            </div>

            <div className="formSection">
              <div className="select-container">
                <div className="form">
                  <label>Select Choices</label>

                  <select
                    name="category"
                    id="category"
                    className="form-field-select"
                    value={formik.values.category}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">---Category---</option>
                    {categoriesList.map((option) => (
                      <option
                        key={option._id || option.id}
                        value={option._id || option.id}
                      >
                        {option.title || option.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.category && formik.errors.category && (
                    <p className="error-text">{formik.errors.category}</p>
                  )}
                  <select
                    name="industry"
                    id="industry"
                    className="form-field-select"
                    value={formik.values.industry}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">---Industry---</option>
                    {industriesList.map((option) => (
                      <option
                        key={option._id || option.id}
                        value={option._id || option.id}
                      >
                        {option.title || option.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.industry && formik.errors.industry && (
                    <p className="error-text">{formik.errors.industry}</p>
                  )}

                  <select
                    name="client"
                    id="client"
                    className="form-field-select"
                    value={formik.values.client}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">---Client---</option>
                    {clientList.map((option) => (
                      <option
                        key={option._id || option.id}
                        value={option._id || option.id}
                      >
                        {option.name || option.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.client && formik.errors.client && (
                    <p className="error-text">{formik.errors.client}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="formSection">
            <div className="UploadImages">
              <div className="bulkThumbnail">
                <label>
                  Bulk Image Upload (
                  {formatPlanCap(
                    bulkImages.length,
                    planLimits.maxCaseStudyAttachments,
                  )}
                  )
                </label>
                <div
                  className={`bulkUpload ${isDragging ? "dragging" : ""}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    border: isDragging
                      ? "2px dashed #0A4DA2"
                      : "2px dashed transparent",
                    backgroundColor: isDragging ? "#f0f7ff" : "transparent",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div className="bulk-images-grid">
                    {bulkImages.map((img, index) => (
                      <div
                        key={index}
                        className="bulk-image-card"
                        style={{ position: "relative" }}
                      >
                        <img
                          src={img}
                          alt={`upload-${index}`}
                          onClick={() => handleImageClick(img, index)}
                          style={{
                            cursor: "pointer",
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(index);
                          }}
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                            background: "rgba(255, 255, 255, 0.8)",
                            border: "none",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "#ff4d4f",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          <RiDeleteBin5Line size={14} />
                        </button>
                      </div>
                    ))}
                    <div
                      className="add-new-card"
                      onClick={() => {
                        if (
                          Number.isFinite(planLimits.maxCaseStudyAttachments) &&
                          bulkImageFiles.length >=
                            planLimits.maxCaseStudyAttachments
                        ) {
                          toast.error(
                            `Your plan allows ${planLimits.maxCaseStudyAttachments} image(s) per case study.`,
                          );
                          return;
                        }
                        document.getElementById("bulkImageInput").click();
                      }}
                    >
                      <span
                        style={{
                          textAlign: "center",
                          fontSize: "clamp(0.5rem, 2.5vw, 0.8rem)",
                        }}
                      >
                        Drag & Drop or Click to Upload
                      </span>
                    </div>
                  </div>
                  <input
                    type="file"
                    multiple
                    id="bulkImageInput"
                    accept="image/jpeg,image/jpg,image/png"
                    style={{ display: "none" }}
                    onChange={handleBulkImageChange}
                    onBlur={() => formik.setFieldTouched("bulkImages", true)}
                  />
                  {formik.touched.bulkImages && formik.errors.bulkImages && (
                    <p className="error-text">{formik.errors.bulkImages}</p>
                  )}
                  {/* <button className="addBtn" onClick={uploadImages}>
                  {" "}
                  <IoCloudUploadOutline style={{ marginRight: "8px" }} /> Upload
                </button> */}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <button
              type="button"
              className="CancelButton"
              onClick={() => navigate("/case-studies")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="CommonButton"
              disabled={
                isCreating ||
                isUpdating ||
                (!isEditMode && !planLimits.caseStudies.canCreate)
              }
            >
              {isCreating || isUpdating
                ? "Saving..."
                : isEditMode
                  ? "Update Case Study"
                  : "Create Case Study"}
            </button>
          </div>
        </form>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        className="image-preview-modal"
      >
        <div className="image-modal-wrapper">
          <img
            src={selectedImage}
            alt="Preview"
            className="modal-preview-image"
          />
          <button
            className="modal-delete-btn"
            onClick={() => {
              handleDeleteImage(selectedImageIndex);
              closeModal();
            }}
            title="Delete image"
          >
            <RiDeleteBin5Line />
          </button>
        </div>
      </Modal>
    </>
  );
};

export default AddCaseStudy;
